/**
 * @see https://github.com/js-cookie/js-cookie
 */
interface CookieAttributes {
    /**
     * Define when the cookie will be removed. Value can be a Number
     * which will be interpreted as days from time of creation or a
     * Date instance. If omitted, the cookie becomes a session cookie.
     */
    expires?: number | Date | undefined | string

    /**
     * Define the path where the cookie is available. Defaults to '/'
     */
    path?: string | undefined;

    /**
     * Define the domain where the cookie is available. Defaults to
     * the domain of the page where the cookie was created.
     */
    domain?: string | undefined;

    /**
     * A Boolean indicating if the cookie transmission requires a
     * secure protocol (https). Defaults to false.
     */
    secure?: boolean | undefined;

    /**
     * Asserts that a cookie must not be sent with cross-origin requests,
     * providing some protection against cross-site request forgery
     * attacks (CSRF)
     */
    sameSite?: "strict" | "Strict" | "lax" | "Lax" | "none" | "None" | undefined;

    /**
     * An attribute which will be serialized, conformably to RFC 6265
     * section 5.2.
     */
    [property: string]: any;
}

interface CookiesStatic<T = string> {
    readonly attributes: CookieAttributes;
    readonly converter: Required<Converter<string>>;
    /**
     * Create a cookie
     */
    set(name: string, value: string | T, options?: CookieAttributes): string | undefined;

    /**
     * Read cookie
     */
    get(name: string): string | T | undefined;

    /**
     * Read all available cookies
     */
    get(): { [key: string]: string };

    /**
     * Delete cookie
     */
    remove(name: string, options?: CookieAttributes): void;

    /**
     * Cookie attribute defaults can be set globally by creating an
     * instance of the api via withAttributes(), or individually for
     * each call to Cookies.set(...) by passing a plain object as the
     * last argument. Per-call attributes override the default attributes.
     */
    withAttributes(attributes: CookieAttributes): CookiesStatic<T>;

    /**
     * Create a new instance of the api that overrides the default
     * decoding implementation. All methods that rely in a proper
     * decoding to work, such as Cookies.remove() and Cookies.get(),
     * will run the converter first for each cookie. The returned
     * string will be used as the cookie value.
     */
    withConverter<TConv = string>(converter: Converter<TConv>): CookiesStatic<TConv>;
}

interface Converter<TConv = string> {
    write: CookieWriteConverter<TConv>;
    read: CookieReadConverter<TConv>;
}

type CookieWriteConverter<T> = (value: string | T, name: string) => string;
type CookieReadConverter<T> = (value: string, name: string) => string | T;



const defaultConverter: Converter<string> = {
    read: function (value: string) {
        if (value[0] === '"') {
            value = value.slice(1, -1)
        }
        return value.replace(/(%[\dA-F]{2})+/gi, decodeURIComponent)
    },
    write: function (value: string) {
        return encodeURIComponent(value).replace(
            /%(2[346BF]|3[AC-F]|40|5[BDE]|60|7[BCD])/g,
            decodeURIComponent
        )
    }
}

function init(converter: Converter, defaultAttributes: CookieAttributes) : CookiesStatic {
    function set(name: string, value: string, attributes?: CookieAttributes): string | undefined {
        if (typeof document === 'undefined') {
            return
        }

        attributes = Object.assign({}, defaultAttributes, attributes)

        if (typeof attributes.expires === 'number') {
            attributes.expires = new Date(Date.now() + attributes.expires * 864e5)
        }
        if (attributes.expires) {
            attributes.expires = (attributes.expires as Date).toUTCString()
        }

        name = encodeURIComponent(name)
            .replace(/%(2[346B]|5E|60|7C)/g, decodeURIComponent)
            .replace(/[()]/g, escape)

        let stringifiedAttributes = ''
        for (let attributeName in attributes) {
            if (!attributes[attributeName]) {
                continue
            }

            stringifiedAttributes += '; ' + attributeName

            if (attributes[attributeName] === true) {
                continue
            }

            // Considers RFC 6265 section 5.2:
            // ...
            // 3.  If the remaining unparsed-attributes contains a %x3B (";")
            //     character:
            // Consume the characters of the unparsed-attributes up to,
            // not including, the first %x3B (";") character.
            // ...
            stringifiedAttributes += '=' + attributes[attributeName].split(';')[0]
        }

        return (document.cookie =
            name + '=' + converter.write(value, name) + stringifiedAttributes)
    }

    function get(name?: string): string | { [key: string]: string } | undefined {
        if (typeof document === 'undefined' || (arguments.length && !name)) {
            return
        }

        // To prevent the for loop in the first place assign an empty array
        // in case there are no cookies at all.
        const cookies = document.cookie ? document.cookie.split('; ') : []
        const jar: { [key: string]: string } = {}
        for (let i = 0; i < cookies.length; i++) {
            const parts = cookies[i].split('=')
            const value = parts.slice(1).join('=')

            try {
                const found = decodeURIComponent(parts[0])
                if (!(found in jar)) jar[found] = converter.read(value, found)
                if (name === found) {
                    break
                }
            } catch {
                // Do nothing...
            }
        }

        return name ? jar[name] : jar
    }

    return Object.create(
        {
            set,
            get,
            remove: function (name: string, attributes?: CookieAttributes) {
                set(
                    name,
                    '',
                    Object.assign({}, attributes, {
                        expires: -1
                    })
                )
            },
            withAttributes: function (this: CookiesStatic, attributes: CookieAttributes) {
                return init(this.converter, Object.assign({}, this.attributes, attributes))
            },
            withConverter: function (this: CookiesStatic, converter: CookiesStatic) {
                return init(Object.assign({}, this.converter, converter), this.attributes)
            }
        },
        {
            attributes: { value: Object.freeze(defaultAttributes) },
            converter: { value: Object.freeze(converter) }
        }
    )
}

const Cookies = init(defaultConverter, { path: '/' })

export default Cookies
